import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthSession } from '@app/context/auth-session';
import { buildLoginRedirectPath } from '@app/utils/auth-redirect';
import type { Membershipschema, Userschema } from '@app/types/sdk';

const ProfilePage = () => {
  const { user: me, tokens, loading, refreshUser } = useAuthSession();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!tokens) {
      navigate(buildLoginRedirectPath(location));
    }
  }, [location, navigate, tokens]);

  useEffect(() => {
    if (!me && tokens) {
      refreshUser().catch((error) => {
        console.error('[profile] failed to refresh user', error);
      });
    }
  }, [me, refreshUser, tokens]);

  if (!me || loading) {
    return (
      <main className="auth-layout">
        <section className="auth-card">
          <header className="auth-card__header">
            <h1 className="auth-title">Loading your workspace…</h1>
          </header>
        </section>
      </main>
    );
  }

  const profile = me.user as Userschema & { displayName?: string | null };
  const memberships = (me.memberships ?? []) as Array<
    Membershipschema & {
      orgName?: string | null;
      rolesResolved?: Array<{ id?: string | null; slug?: string | null; name?: string | null }>;
      status?: string;
    }
  >;

  return (
    <main className="profile-layout">
      <header className="profile-header">
        <div>
          <h1>{profile.displayName ?? profile.name ?? profile.email ?? 'Your profile'}</h1>
          <p>{profile.email}</p>
        </div>
        <button type="button" className="auth-ghost" onClick={() => navigate('/logout')}>
          Log out
        </button>
      </header>

      <section className="profile-section">
        <h2>Organization memberships</h2>
        {memberships.length === 0 ? (
          <p className="org-note">You have no active memberships yet.</p>
        ) : (
          <ul className="membership-list">
            {memberships.map((membership, index) => {
              const roleNames = (membership.rolesResolved ?? [])
                .map((role) => role?.name ?? role?.slug ?? role?.id ?? '')
                .filter((value) => value.length > 0);
              return (
                <li key={membership.id ?? membership.orgId ?? `membership-${index}`}>
                  <div>
                    <strong>{membership.orgName ?? membership.orgId}</strong>
                    {roleNames.length > 0 && <span className="role-chip">{roleNames.join(', ')}</span>}
                  </div>
                  <span className="membership-status">{membership.status ?? 'active'}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
};

export default ProfilePage;
